import { ActionIcon, Stack } from '@mantine/core';
import {
  IconBuildingWarehouse,
  IconCamera,
  IconCircleArrowUp,
  IconDashboard,
  IconDeviceDesktopAnalytics,
  IconFlower,
  IconHelp,
  IconHierarchy3,
  IconLogout,
  IconMap,
  IconPolygon,
  IconSettings,
  IconShield,
  IconUserCircle,
  IconUsers,
} from '@tabler/icons-react';
import { getRouteApi, Link, LinkProps } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import NavbarLink from '../navbar-link/navbar-link';

import classes from './app-navbar.module.css';

import { useAuth } from '@auth/data-access';
import { hasAdminAccess, hasDistriAccess } from '@data-access';
import { LangSelect } from '@ui';

const route = getRouteApi('/_app');

type Link = {
  label: string;
  to: LinkProps['to'];
  icon: typeof IconDashboard;
  hasAccess?: boolean;
};

const filterLinks = (links: Link[]) => {
  return links.filter((link) => {
    if (typeof link.hasAccess === 'undefined') return true;
    return link.hasAccess;
  });
};

export function AppNavbar() {
  const navigate = route.useNavigate();
  const { logout, user } = useAuth();
  const { t } = useTranslation();

  const links: Link[] = [
    {
      to: '/map',
      label: t('app.nav.map'),
      icon: IconMap,
    },
    {
      to: '/fields',
      label: t('app.nav.fields'),
      icon: IconPolygon,
    },
    {
      to: '/interculture',
      label: t('app.nav.interculture'),
      icon: IconFlower,
    },
    {
      to: '/harvest-proof',
      label: t('app.nav.harvestProof'),
      icon: IconCamera,
      hasAccess: hasDistriAccess(user?.user_access),
    },
    {
      to: '/insurance',
      label: t('app.nav.insurance'),
      icon: IconShield,
      hasAccess: hasAdminAccess(user?.user_access),
    },
    {
      to: '/transfer-yield-map',
      label: t('app.nav.transferYieldMap'),
      icon: IconCircleArrowUp,
    },
  ];

  const bottomLinks: Link[] = [
    {
      to: '/settings',
      label: t('app.nav.settings'),
      icon: IconSettings,

      hasAccess: hasAdminAccess(user?.user_access),
    },
    {
      to: '/dashboard',
      label: t('app.nav.dashboard'),
      icon: IconDeviceDesktopAnalytics,
      hasAccess: hasAdminAccess(user?.user_access),
    },
    {
      to: '/organisations',
      label: t('app.nav.organisations'),
      icon: IconHierarchy3,
      hasAccess: hasAdminAccess(user?.user_access),
    },
    {
      to: '/users',
      label: t('app.nav.users'),
      icon: IconUsers,
      hasAccess: hasDistriAccess(user?.user_access),
    },
    {
      to: '/accounts',
      label: t('app.nav.accounts'),
      icon: IconBuildingWarehouse,
      hasAccess: hasDistriAccess(user?.user_access),
    },
  ];

  const responsiveLinks: Link[] = [
    {
      to: '/support',
      label: t('app.nav.help'),
      icon: IconHelp,
    },
    {
      to: '/profile',
      label: t('app.nav.profile'),
      icon: IconUserCircle,
    },
  ];

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      navigate({ to: '/' });
    }
  };

  return (
    <Stack className={classes.container} justify="space-between">
      <Stack gap="xs">
        {filterLinks(links).map((link) => (
          <NavbarLink {...link} key={link.label} />
        ))}
      </Stack>
      <Stack gap="xs">
        {filterLinks(bottomLinks).map((link) => (
          <NavbarLink {...link} key={link.label} />
        ))}
        {responsiveLinks.map((link) => (
          <NavbarLink {...link} hiddenFrom="xs" key={link.label} />
        ))}
        <Stack align="center" gap="xs" hiddenFrom="md">
          <LangSelect />

          <ActionIcon onClick={handleLogout} size={40} variant="filled">
            <IconLogout style={{ width: '70%', height: '70%' }} />
          </ActionIcon>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default AppNavbar;
